// src/app/auth/auth.component.ts

import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressBar } from '@angular/material/progress-bar';
import { LoginService } from '@services/login.service';
import { LogService } from '@services/log.service';
import { PreferencesService } from '@services/preferences.service';

@Component({
  selector: 'app-auth',
  template: `
    <section class="d-flex flex-column align-items-center justify-content-center auth-section">
        <div class="loader-container">
            <div>Please wait while we are fetching your details...</div>
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>
    </section>
  `,
  styles: [`
    .auth-section {
        height: calc(100vh - (64px + 88px )); /* 64px for the header and 88px for the footer */

        .loader-container {
            width: 450px;
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
    }
  `],
  imports: [MatProgressBar],
})
export class AuthComponent implements OnInit {
  private readonly logService = inject(LogService);
  private readonly preferencesService = inject(PreferencesService);

  constructor(
    private route: ActivatedRoute, 
    private router: Router, 
    private loginService: LoginService
  ) {}

// https://dev.opendata.energy/auth?link=/ZXlKaGJHY2lPaUpJVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SmxiV0ZwYkNJNkluTmhiblJvYjNOb0xuSkFaR2xuYVhSaGJHRndhV055WVdaMExtTnZiU0lzSW1GMWRHaERhR0ZzYkdWdVoyVWlPaUl5TlRabU5XUmpPUzAzTjJZNExUUXpaak10T1RJeE9DMDJZVGhqTXpCbFpHUmtZbVFpTENKaFoyVnVkRVZ0WVdsc0lqb2lZV2RsYm5SQVkyOXRjR0Z1ZVM1amIyMGlMQ0poWjJWdWRFOXlaeUk2SWtWdVpYSm5lU0JUYjJ4MWRHbHZibk1nVEhSa0lpd2lZM1Z6ZEc5dFpYSkZiV0ZwYkNJNkluTmhiblJvYjNOb0xuSkFaR2xuYVhSaGJHRndhV055WVdaMExtTnZiU0lzSW1ScGMzQnNZWGxGYldGcGJDSTZJbk5oYm5Sb2IzTm9MbkpBWkdsbmFYUmhiR0Z3YVdOeVlXWjBMbU52YlNJc0ltMWxkR1Z5VG5WdFltVnlJam9pTVRFd01EQXpPVE0xT1RRNE1pSXNJbWxoZENJNk1UYzBPVGN3T0RVMU1uMC5hVXZNOW82NUJ1SFBlX3dXUEU4SjE5cnlGcVdDWUJLaHU3QjNaQkVFYmFB
// http://localhost:4200/auth?link=ZXlKaGJHY2lPaUpJVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SmxiV0ZwYkNJNkluTmhiblJvYjNOb0xuSkFaR2xuYVhSaGJHRndhV055WVdaMExtTnZiU0lzSW1GMWRHaERhR0ZzYkdWdVoyVWlPaUl5TlRabU5XUmpPUzAzTjJZNExUUXpaak10T1RJeE9DMDJZVGhqTXpCbFpHUmtZbVFpTENKaFoyVnVkRVZ0WVdsc0lqb2lZV2RsYm5SQVkyOXRjR0Z1ZVM1amIyMGlMQ0poWjJWdWRFOXlaeUk2SWtWdVpYSm5lU0JUYjJ4MWRHbHZibk1nVEhSa0lpd2lZM1Z6ZEc5dFpYSkZiV0ZwYkNJNkluTmhiblJvYjNOb0xuSkFaR2xuYVhSaGJHRndhV055WVdaMExtTnZiU0lzSW1ScGMzQnNZWGxGYldGcGJDSTZJbk5oYm5Sb2IzTm9MbkpBWkdsbmFYUmhiR0Z3YVdOeVlXWjBMbU52YlNJc0ltMWxkR1Z5VG5WdFltVnlJam9pTVRFd01EQXpPVE0xT1RRNE1pSXNJbWxoZENJNk1UYzBPVGN3T0RVMU1uMC5hVXZNOW82NUJ1SFBlX3dXUEU4SjE5cnlGcVdDWUJLaHU3QjNaQkVFYmFB

  async ngOnInit(): Promise<void> {
    try {
      this.logService.logPageView('open-magic-link', {
        queryParams: this.route.snapshot.queryParams
      });
      // 1. Retrieve the 'link' parameter from query params
      this.route.queryParams.subscribe(async (params) => {
        const link = params['link'];
        
        if (!link) {
          console.error('No link parameter found in query params.');
          this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
          return;
        }

        // 2. Complete the authentication flow using LoginService
        const authResult = await this.loginService.completeAuthenticationFlow(link);
        const { userDetails, redirectInfo: queryParams, email } = authResult;
        
        if (!authResult.success) {
          console.error('Authentication failed:', authResult.error);
          // Optionally, navigate to an error page or show error message
          if (authResult.error === 'UNVERIFIED') {
            // Placeholder user created by a consent request: /register resolves the consent request,
            // prefills the form and, because the link is valid, skips the login email and returns here.
            console.warn('User is not verified, redirecting to register page.');
            this.router.navigate(['/register'], {
              queryParams: { link, ...(queryParams?.consentRequestId && { consentRequestId: queryParams.consentRequestId }) },
              queryParamsHandling: 'merge',
            });
          } else {
            this.router.navigate(['/signin'], { 
              queryParams: { error: authResult.error },
              queryParamsHandling: 'merge' 
            });
          }
          return;
        }
        
        if (!userDetails) {
          console.error('No user details received from authentication');
          this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
          return;
        }

        // 3. Navigate based on user verification status and redirect info
        if (userDetails.isVerified) {
          this.preferencesService.setStandaloneMode(true); // Standalone mode for regular auth
          if (queryParams?.consentRequestId) {
            this.router.navigate(['/meter-details'], { 
              queryParams
            });
          } else {
            this.router.navigate(['/dashboard']);
          }
        } else {
          this.router.navigate(['/register'], { queryParamsHandling: 'merge' });
        }
      });
    } catch (error) {
      console.error('An error occurred during authentication:', error);
      this.router.navigate(['/signin'], { 
        queryParams: { error: 'Authentication failed' },
        queryParamsHandling: 'merge' 
      });
    }
  }
}
